import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  Input,
  Output,
  inject,
} from '@angular/core'
import { BehaviorSubject } from 'rxjs'
import {
  DataContextBuilder,
  ElderFormFieldDenseDirective,
  ElderNumberCellDirective,
  ElderPaneComponent,
  ElderPaneContentComponent,
  ElderPaneSubtitleComponent,
  ElderQuantityPipe,
  ElderSelectComponent,
  ElderTableComponent,
  ElderToastService,
  Filter,
  IDataContext,
  IsoIntervalPipe,
  IsoIntervalStr,
  Quantity,
} from '@elderbyte/ngx-starter'
import {
  Delivery,
  DeliveryOrderSchedulingState,
  DeliveryOrderTransportState,
} from '../../delivery/delivery'
import { DeliveryService } from '../../delivery/delivery.service'
import { DeliveryFilterBuilder } from '../../delivery/delivery-filter-builder'
import { LoggerFactory } from '@elderbyte/ts-logger'
import { ElderTranslatePipe } from '@elderbyte/ngx-starter'
import { StateIconComponent } from '../../../common/state-icon/state-icon.component'
import { DeliverySelectDirective } from '../../delivery/delivery-select.directive'
import { MatFormField } from '@angular/material/form-field'
import {
  MatCell,
  MatCellDef,
  MatColumnDef,
  MatHeaderCell,
  MatHeaderCellDef,
} from '@angular/material/table'
import { MatSort } from '@angular/material/sort'
import { AsyncPipe } from '@angular/common'
import { ProdOrderTransit, TransitAssignment, TransitType } from '../prod-order-transit'
import { ProdOrderTransitService } from '../prod-order-transit.service'

@Component({
  selector: 'app-prod-order-transit-detail',
  templateUrl: './prod-order-transit-detail.component.html',
  styleUrls: ['./prod-order-transit-detail.component.scss'],
  imports: [
    ElderTableComponent,
    MatSort,
    MatColumnDef,
    MatHeaderCellDef,
    MatHeaderCell,
    MatCellDef,
    MatCell,
    MatFormField,
    ElderFormFieldDenseDirective,
    ElderSelectComponent,
    DeliverySelectDirective,
    StateIconComponent,
    ElderNumberCellDirective,
    AsyncPipe,
    ElderTranslatePipe,
    IsoIntervalPipe,
    ElderQuantityPipe,
    ElderPaneComponent,
    ElderPaneContentComponent,
    ElderPaneSubtitleComponent,
  ],
})
export class ProdOrderTransitDetailComponent {
  /***************************************************************************
   *                                                                         *
   * Fields                                                                  *
   *                                                                         *
   **************************************************************************/

  private readonly log = LoggerFactory.getLogger(this.constructor.name)
  private deliveryService = inject(DeliveryService)
  private prodOrderTransitService = inject(ProdOrderTransitService)
  private toastService = inject(ElderToastService)
  public prodOrderTransit$ = new BehaviorSubject<ProdOrderTransit>(null)
  public assignmentsCtx: IDataContext<TransitAssignment>
  public deliveriesCtx: IDataContext<Delivery>

  @Output()
  public readonly transitAssignmentChange: EventEmitter<TransitAssignment> =
    new EventEmitter<TransitAssignment>()

  /***************************************************************************
   *                                                                         *
   * Constructor                                                             *
   *                                                                         *
   **************************************************************************/

  constructor() {
    this.deliveriesCtx = DataContextBuilder.start<Delivery>()
      .pageSize(30)
      .reloadOnLocalChanges()
      .build(this.deliveryService)
  }

  /***************************************************************************
   *                                                                         *
   * Properties                                                              *
   *                                                                         *
   **************************************************************************/

  @Input()
  public set prodOrderTransit(order: ProdOrderTransit) {
    this.prodOrderTransit$.next(order)
    this.initWorkOrderCtx(order)
    this.initDeliveriesCtx(order)
  }

  public get prodOrderTransit(): ProdOrderTransit {
    return this.prodOrderTransit$.getValue()
  }

  /***************************************************************************
   *                                                                         *
   * Public API                                                              *
   *                                                                         *
   **************************************************************************/

  public onDeliveryAssigned(TransitAssignment: TransitAssignment): void {
    this.patchWorkOrder(TransitAssignment)
  }

  /***************************************************************************
   *                                                                         *
   * Private Methods                                                         *
   *                                                                         *
   **************************************************************************/

  public patchWorkOrder(toUpdate: TransitAssignment) {
    this.log.debug('Updating transitAssignment:', toUpdate)
    this.prodOrderTransitService.patchAssignment(this.prodOrderTransit, toUpdate).subscribe({
      next: (updated) => {
        this.toastService.info('Successfully patched assignment')
        this.reloadProdOrderTransit()
        const updatedAssignment = updated.assignments.find(
          (assignment) => assignment.workOrderId === toUpdate.workOrderId
        )
        this.transitAssignmentChange.emit(updatedAssignment!)
      },
      error: (err) => this.toastService.error('messages.persisting_failed', err),
    })
  }

  private reloadProdOrderTransit(): void {
    this.prodOrderTransitService.findById(this.prodOrderTransit.id).subscribe({
      next: (prodOrderTransit) => (this.prodOrderTransit = prodOrderTransit),
      error: (err) => this.toastService.error('messages.loading_failed', err),
    })
  }

  private initWorkOrderCtx(order: ProdOrderTransit): void {
    if (order && order.assignments) {
      this.assignmentsCtx = DataContextBuilder.start<TransitAssignment>().buildLocal(
        order.assignments
      )

      this.assignmentsCtx.start()
    }
  }

  private initDeliveriesCtx(order: ProdOrderTransit): void {
    if (order) {
      const refNumber = order.prodOrderId

      const targetFacility = order.type === TransitType.INTER_SITE ? order.targetFacilityId : null

      const filters = DeliveryFilterBuilder.start()
        .facility(order.sourceFacilityId)
        .targetFacility(targetFacility)
        .orderReferenceNumbers(refNumber)
        .build()

      this.replaceDeliveriesCtxFilters(filters)
    }
  }

  private replaceDeliveriesCtxFilters(filters: Filter[]): void {
    if (this.deliveriesCtx.isStarted) {
      this.deliveriesCtx.filter.replaceFilters(filters)
    } else {
      this.deliveriesCtx.start([], filters)
    }
  }

  mapScheduleStateToIcon(deliveryOrderSchedulingState: DeliveryOrderSchedulingState): string {
    switch (deliveryOrderSchedulingState) {
      case 'OPEN':
        return 'radio_button_checked'
      case 'IN_PROGRESS':
        return 'play_circle_outline'
      case 'SCHEDULED':
        return 'check_circle_outline'
      case 'UNKNOWN':
      default:
        return 'help_outline'
    }
  }

  mapTransportStateToIcon(transportState: DeliveryOrderTransportState): string {
    switch (transportState) {
      case 'OPEN':
        return 'radio_button_checked'
      case 'PARTIALLY_COMPLETED':
        return 'play_circle_outline'
      case 'COMPLETED':
        return 'check_circle_outline'
      case 'UNKNOWN':
      default:
        return 'help_outline'
    }
  }

  getScheduleInterval(transitAssignment: TransitAssignment): IsoIntervalStr | undefined {
    return transitAssignment.workOrder.scheduleInterval
  }

  getDescription(transitAssignment: TransitAssignment): string {
    return transitAssignment.workOrder.description
  }

  getProjectDescription(transitAssignment: TransitAssignment): string {
    return transitAssignment.workOrder.projectDescription
  }

  getManufacturingFacility(transitAssignment: TransitAssignment): string {
    return transitAssignment.workOrder.manufactureFacility
  }

  getTotalWeight(transitAssignment: TransitAssignment): Quantity | undefined {
    return transitAssignment.workOrder.totalWeight
  }

  getDeliveryPickupTimeFrame(transitAssignment: TransitAssignment): string | undefined {
    return transitAssignment.delivery?.pickupTimeFrame
  }

  getSchedulingState(
    transitAssignment: TransitAssignment
  ): DeliveryOrderSchedulingState | undefined {
    return transitAssignment.delivery?.schedulingState
  }

  getTransportState(transitAssignment: TransitAssignment): DeliveryOrderTransportState | undefined {
    return transitAssignment.delivery?.transportState
  }
}
